import React from "react";
import ColorField from "./ColorField";

// Color palette harmonised with the site's cream/Christmas background.
// Each entry: bg (base), dark (shadow/gradient), text (readable on bg).
export const BUTTON_COLORS = {
  // Cores primárias (para crianças)
  prim_red: { label: "● Vermelho (primária)", bg: "#C1121F", dark: "#7A0B14", text: "#FFFFFF" },
  prim_blue: { label: "● Azul (primária)", bg: "#3B82F6", dark: "#1D4ED8", text: "#FFFFFF" },
  prim_yellow: { label: "● Amarelo (primária)", bg: "#F2B705", dark: "#B8890B", text: "#0B1D3A" },
  // Misturas principais (cores secundárias)
  mix_orange: { label: "◐ Laranja (vermelho + amarelo)", bg: "#C8A24B", dark: "#9A7A2E", text: "#0B1D3A" },
  mix_green: { label: "◐ Verde (azul + amarelo)", bg: "#1B5E3A", dark: "#0F3D25", text: "#FFFFFF" },
  mix_purple: { label: "◐ Roxo (vermelho + azul)", bg: "#6366F1", dark: "#3730A3", text: "#FFFFFF" },
  // Misturas terciárias (primária + secundária)
  mix_red_orange: { label: "◔ Vermelho-alaranjado (vermelho + laranja)", bg: "#C1121F", dark: "#7A0B14", text: "#FFFFFF" },
  mix_yellow_orange: { label: "◔ Amarelo-alaranjado (amarelo + laranja)", bg: "#F2B705", dark: "#B8890B", text: "#0B1D3A" },
  mix_yellow_green: { label: "◔ Amarelo-esverdeado / lima (amarelo + verde)", bg: "#1B5E3A", dark: "#0F3D25", text: "#FFFFFF" },
  mix_blue_green: { label: "◔ Azul-esverdeado / turquesa (azul + verde)", bg: "#005F73", dark: "#003847", text: "#FFFFFF" },
  mix_blue_purple: { label: "◔ Azul-arroxeado / índigo (azul + roxo)", bg: "#6366F1", dark: "#3730A3", text: "#FFFFFF" },
  mix_red_purple: { label: "◔ Vermelho-arroxeado / magenta (vermelho + roxo)", bg: "#6366F1", dark: "#3730A3", text: "#FFFFFF" },
  coral: { label: "Coral", bg: "#1B5E3A", dark: "#0F3D25", text: "#FFFFFF" },
  holly: { label: "Vermelho-azevinho", bg: "#C1121F", dark: "#7A0B14", text: "#FFFFFF" },
  gold: { label: "Dourado", bg: "#C8A24B", dark: "#9A7A2E", text: "#0B1D3A" },
  pine: { label: "Verde-pinho", bg: "#1B5E3A", dark: "#0F3D25", text: "#FFFFFF" },
  midnight: { label: "Azul-escuro", bg: "#0B1D3A", dark: "#020610", text: "#FFFFFF" },
  teal: { label: "Azul-petróleo", bg: "#005F73", dark: "#003847", text: "#FFFFFF" },
  purple: { label: "Roxo", bg: "#6366F1", dark: "#3730A3", text: "#FFFFFF" },
  sky: { label: "Azul-céu", bg: "#3B82F6", dark: "#1D4ED8", text: "#FFFFFF" },
  orange: { label: "Laranja", bg: "#C8A24B", dark: "#9A7A2E", text: "#0B1D3A" },
  pink: { label: "Rosa-doce", bg: "#C1121F", dark: "#7A0B14", text: "#FFFFFF" },
  mint: { label: "Menta", bg: "#1B5E3A", dark: "#0F3D25", text: "#FFFFFF" },
  emerald: { label: "Esmeralda", bg: "#1B5E3A", dark: "#0F3D25", text: "#FFFFFF" },
  burgundy: { label: "Borgonha", bg: "#7A0B14", dark: "#450A0A", text: "#FFFFFF" },
  silver: { label: "Prata", bg: "#CBD5E1", dark: "#94A3B8", text: "#0B1D3A" },
  snow: { label: "Branco-neve", bg: "#FFFFFF", dark: "#CBD5E1", text: "#0B1D3A" },
};

export const isHexColor = (c) => typeof c === "string" && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(c);

function _hexToRgb(hex) {
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}
function _darken(hex, f) {
  const [r, g, b] = _hexToRgb(hex);
  const d = (x) => Math.max(0, Math.round(x * (1 - f)));
  return `#${[d(r), d(g), d(b)].map((x) => x.toString(16).padStart(2, "0")).join("")}`;
}
function _contrastText(hex) {
  const [r, g, b] = _hexToRgb(hex);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.62 ? "#0B1D3A" : "#FFFFFF";
}
export function paletteFromColor(color) {
  if (BUTTON_COLORS[color]) return BUTTON_COLORS[color];
  if (isHexColor(color)) return { bg: color, dark: _darken(color, 0.4), text: _contrastText(color) };
  return BUTTON_COLORS.coral;
}

// Select with named palette + always-visible custom hex code field.
export function ColorSelect({ value, onChange, testId, className }) {
  const isCustom = !BUTTON_COLORS[value];
  const currentHex = isHexColor(value) ? value : (BUTTON_COLORS[value]?.bg || "#FF6B6B");
  return (
    <>
      <select value={isCustom ? "custom" : value} data-testid={testId} className={className}
        onChange={(e) => onChange(e.target.value === "custom" ? currentHex : e.target.value)}>
        {Object.entries(BUTTON_COLORS).map(([v, c]) => <option key={v} value={v}>{c.label}</option>)}
        <option value="custom">✎ Cor personalizada (código)</option>
      </select>
      <ColorField value={currentHex} onChange={(e) => onChange(e.target.value)}
        className="w-full mt-1 h-9 rounded-lg border border-[#0B1D3A]/15"
        data-testid={testId ? `${testId}-hex` : undefined} />
    </>
  );
}

export const BUTTON_FORMATS = [
  { v: "clay", label: "Clay 3D" },
  { v: "flat", label: "Plano" },
  { v: "outline", label: "Contorno" },
  { v: "pill", label: "Pílula" },
  { v: "glass", label: "Vidro" },
  { v: "gradient", label: "Gradiente" },
  { v: "neon", label: "Néon" },
  { v: "frost", label: "Geada" },
  { v: "candy", label: "Bengala doce" },
  { v: "soft", label: "Suave (relevo)" },
];

export const BUTTON_EFFECTS = [
  { v: "none", label: "Nenhum" },
  { v: "lift", label: "Elevar" },
  { v: "glow", label: "Brilho" },
  { v: "shine", label: "Reflexo" },
  { v: "pulse", label: "Pulsar" },
  { v: "bounce-icon", label: "Ícone saltita" },
  { v: "wiggle", label: "Abanar" },
  { v: "tilt", label: "Inclinar" },
  { v: "zoom", label: "Zoom" },
  { v: "float", label: "Flutuar" },
  { v: "jelly", label: "Gelatina" },
  { v: "sparkle", label: "Cintilar" },
  { v: "border-run", label: "Borda animada" },
  { v: "shimmer", label: "Brilho contínuo" },
  { v: "glow-pulse", label: "Halo pulsante" },
  { v: "heartbeat", label: "Batida" },
  { v: "swing", label: "Balançar" },
  { v: "spin-icon", label: "Ícone roda" },
];

export default function ClayCard({
  onClick,
  color = "coral",
  format = "clay",
  effect = "lift",
  icon: Icon,
  title,
  subtitle,
  size = "lg",
  titleSize,
  subtitleSize,
  titleFont,
  subtitleFont,
  className = "",
  style: styleProp,
  testId,
}) {
  const c = paletteFromColor(color);
  const ts = (n) => (n ? { fontSize: `${n}px`, lineHeight: 1.15 } : undefined);
  const style = { "--btn-bg": c.bg, "--btn-dark": c.dark, "--btn-text": c.text, ...(styleProp || {}) };
  const SIZES = {
    "2xs": { pad: "px-2.5 py-1.5", gap: "gap-2", icon: "h-6 w-6", iconSize: 13, title: "text-[11px] md:text-xs", sub: "text-[10px]" },
    xs: { pad: "px-3 py-2", gap: "gap-2", icon: "h-8 w-8", iconSize: 16, title: "text-xs md:text-sm", sub: "text-[11px]" },
    sm: { pad: "px-4 py-3", gap: "gap-3", icon: "h-9 w-9", iconSize: 18, title: "text-sm md:text-base", sub: "text-xs" },
    md: { pad: "px-5 py-5", gap: "gap-3", icon: "h-11 w-11", iconSize: 22, title: "text-base md:text-lg", sub: "text-xs md:text-sm" },
    lg: { pad: "px-6 py-7 md:px-8 md:py-8", gap: "gap-4", icon: "h-12 w-12 md:h-14 md:w-14", iconSize: 28, title: "text-xl md:text-2xl", sub: "text-sm md:text-base" },
  };
  const s = SIZES[size] || SIZES.lg;
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      style={style}
      className={`home-btn fmt-${format} fx-${effect} ${s.pad} ${className}`}
    >
      <div className={`relative z-[1] flex items-start ${s.gap}`}>
        {Icon && (
          <span className={`btn-icon inline-flex ${s.icon} items-center justify-center rounded-2xl shrink-0`}>
            <Icon size={s.iconSize} strokeWidth={2.5} />
          </span>
        )}
        <div className="flex-1">
          <h3 className={`font-${titleFont || "fredoka"} ${s.title} font-bold leading-snug break-words`} style={ts(titleSize)}>{title}</h3>
          {subtitle && (
            <p className={`font-${subtitleFont || "nunito"} ${s.sub} mt-1 opacity-90`} style={ts(subtitleSize)}>{subtitle}</p>
          )}
        </div>
      </div>
    </button>
  );
}
